import { useMemo, useState } from 'react';
import { Chessboard } from 'react-chessboard';
import { useTheme } from 'next-themes';
import { getLegalTargets, needsPromotionChoice, pieceAt, sideToMove, toUci } from '../../lib/utils/chessHelpers';
import type { Color } from '../../types';
import './board.css';

export interface BoardProps {
  fen: string;
  orientation: Color;
  /** Si absent, l'échiquier est en lecture seule (aucune interaction possible). */
  onUserMove?: (uci: string) => void;
  /** Restreint les coups jouables à cette couleur (typiquement le joueur étudié). */
  playableColor?: Color;
  lastMoveSquares?: { from: string; to: string } | null;
  boardId?: string;
  /** Flèche (ex. meilleur coup suggéré) affichée par-dessus le plateau. */
  arrowSquares?: { from: string; to: string } | null;
  /** Cases mises en évidence (ex. pièce capturée dans la séquence de punition). */
  highlightSquares?: string[];
}

// Échiquier vert sauge / crème, assorti à la palette « carnet d'étude ».
const LIGHT = { light: '#ebecd0', dark: '#77855a' };
const DARK = { light: '#779556', dark: '#46512f' };
const SELECTED_BG = 'rgba(232, 163, 60, 0.55)';
const TARGET_DOT = 'radial-gradient(circle, rgba(29,24,17,0.35) 22%, transparent 26%)';
const LAST_MOVE_BG = 'rgba(255, 206, 84, 0.45)';
const HIGHLIGHT_BG = 'rgba(184, 82, 58, 0.5)';

export function Board({
  fen,
  orientation,
  onUserMove,
  playableColor,
  lastMoveSquares,
  boardId,
  arrowSquares,
  highlightSquares,
}: BoardProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [pendingPromotion, setPendingPromotion] = useState<{ from: string; to: string } | null>(null);
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';

  const legalTargets = useMemo(() => (selected ? getLegalTargets(fen, selected) : []), [fen, selected]);

  const interactive = Boolean(onUserMove);
  const boardOrientation: 'white' | 'black' = orientation === 'w' ? 'white' : 'black';

  function reset() {
    setSelected(null);
    setPendingPromotion(null);
  }

  function attemptMoveTo(target: string) {
    if (!selected || !onUserMove) return;
    if (needsPromotionChoice(fen, selected, target)) {
      setPendingPromotion({ from: selected, to: target });
      return;
    }
    onUserMove(toUci(selected, target));
    reset();
  }

  function handleSquareClick({ square }: { square: string }) {
    if (!interactive) return;
    if (pendingPromotion) return; // en attente d'un choix de promotion

    if (selected && legalTargets.some((t) => t.to === square)) {
      attemptMoveTo(square);
      return;
    }

    const piece = pieceAt(fen, square);
    const canSelect = piece && (!playableColor || piece.color === playableColor) && piece.color === sideToMove(fen);
    if (canSelect) {
      setSelected(square);
    } else {
      reset();
    }
  }

  function handlePieceDrop({ sourceSquare, targetSquare }: { sourceSquare: string; targetSquare: string | null }) {
    if (!interactive || !targetSquare) return false;
    setSelected(sourceSquare);
    const targets = getLegalTargets(fen, sourceSquare);
    if (!targets.some((t) => t.to === targetSquare)) {
      reset();
      return false;
    }
    if (needsPromotionChoice(fen, sourceSquare, targetSquare)) {
      setPendingPromotion({ from: sourceSquare, to: targetSquare });
      return false; // on gère la promotion via l'overlay, pas le snap automatique
    }
    onUserMove?.(toUci(sourceSquare, targetSquare));
    reset();
    return true;
  }

  const squareStyles: Record<string, React.CSSProperties> = {};
  if (lastMoveSquares) {
    squareStyles[lastMoveSquares.from] = { background: LAST_MOVE_BG };
    squareStyles[lastMoveSquares.to] = { background: LAST_MOVE_BG };
  }
  for (const sq of highlightSquares ?? []) {
    squareStyles[sq] = { ...(squareStyles[sq] ?? {}), background: HIGHLIGHT_BG };
  }
  if (selected) {
    squareStyles[selected] = { ...(squareStyles[selected] ?? {}), background: SELECTED_BG };
  }
  for (const t of legalTargets) {
    squareStyles[t.to] = {
      ...(squareStyles[t.to] ?? {}),
      backgroundImage: TARGET_DOT,
      backgroundColor: t.isCapture ? 'rgba(184, 82, 58, 0.4)' : undefined,
    };
  }

  const arrows = arrowSquares
    ? [{ startSquare: arrowSquares.from, endSquare: arrowSquares.to, color: 'rgba(232, 163, 60, 0.9)' }]
    : [];

  return (
    <div className="board-wrap">
      <Chessboard
        options={{
          id: boardId ?? 'magnus-board',
          position: fen,
          boardOrientation,
          showNotation: true,
          allowDragging: interactive,
          allowDrawingArrows: false,
          arrows,
          animationDurationInMs: 150,
          boardStyle: {
            borderRadius: 14,
            overflow: 'hidden',
            boxShadow: dark ? '0 14px 36px -14px rgba(0,0,0,0.6)' : '0 14px 36px -14px rgba(20,20,20,0.4)',
          },
          darkSquareStyle: { backgroundColor: dark ? DARK.dark : DARK.light },
          lightSquareStyle: { backgroundColor: dark ? LIGHT.dark : LIGHT.light },
          squareStyles,
          onSquareClick: handleSquareClick,
          onPieceDrop: handlePieceDrop,
        }}
      />
      {pendingPromotion && (
        <PromotionPicker
          color={sideToMove(fen)}
          onChoose={(piece) => {
            onUserMove?.(toUci(pendingPromotion.from, pendingPromotion.to, piece));
            reset();
          }}
          onCancel={reset}
        />
      )}
    </div>
  );
}

const PROMOTION_PIECES = [
  { code: 'q', label: 'Dame' },
  { code: 'r', label: 'Tour' },
  { code: 'b', label: 'Fou' },
  { code: 'n', label: 'Cavalier' },
];

function PromotionPicker({
  color,
  onChoose,
  onCancel,
}: {
  color: Color;
  onChoose: (piece: string) => void;
  onCancel: () => void;
}) {
  return (
    <div className="promotion-overlay" role="dialog" aria-label="Choix de la promotion">
      <div className="promotion-panel">
        <p>Promouvoir le pion {color === 'w' ? 'blanc' : 'noir'} en :</p>
        <div className="promotion-choices">
          {PROMOTION_PIECES.map((p) => (
            <button key={p.code} type="button" onClick={() => onChoose(p.code)} className="promotion-btn">
              {p.label}
            </button>
          ))}
        </div>
        <button type="button" className="link-btn" onClick={onCancel}>
          Annuler
        </button>
      </div>
    </div>
  );
}
